import React from "react";
import styled from "@emotion/styled";
import { APP_BREAKPOINT, APP_CONTENT_WIDTH } from "src/constants/layout";
import { getSupportedUserLanguage } from "src/lib/i18next";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import AppointmentsList from "src/ui/components/AppointmentsList/AppointmentsList";
import Branding from "src/ui/components/Branding/Branding";
import DailyReadingsWidget from "src/ui/components/DailyReadings/DailyReadingsWidget";
import MealInsightUploadCard from "src/ui/components/MealInsights/MealInsightUploadCard";
import PoweredByWidget from "src/ui/components/PoweredByWidget/PoweredByWidget";
import ProfileWidget from "src/ui/components/ProfileOverview/ProfileWidget";
import AppTopSpacer from "src/ui/components/StyledComponents/AppTopSpacer";
import CollectionTitle from "src/ui/components/StyledComponents/CollectionTitle";
import TaskList from "src/ui/components/TaskList/TaskList";
import Translate from "src/ui/components/Translate/Translate";
import UserEducationalFeed from "src/ui/components/UserEducationalFeed/UserEducationalFeed";
import UserEducationalFeedOld from "src/ui/components/UserEducationalFeed/UserEducationalFeedOld";
import NotificationBell from "src/ui/components/NotificationBell/NotificationBell";
import { isHybridApp } from "src/lib/platform";
import { useBloc } from "src/state/state";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { MedicationSection } from "src/ui/components/MedicationSection/MedicationSection";

const Wrap = styled.div`
  label: HomePageWrap;
  max-width: ${APP_CONTENT_WIDTH}px;
  height: 100%;
  margin-bottom: 5rem;
`;

const Widgets = styled.div`
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  @media (max-width: ${APP_BREAKPOINT}px) {
    & > * {
      flex: 1;
    }
    & > *:not(:first-child):not(:last-child) {
      display: flex;
      justify-content: center;
    }
    & > *:last-child {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const RightWidgets = styled.div`
  display: flex;
  align-items: baseline;
  margin-left: auto;

  @media (max-width: ${APP_BREAKPOINT}px) {
    margin-left: 0;
    align-items: center;
    gap: 0.5rem;
  }
`;

const DashboardPage = () => {
  const { headingRef } = useFocusHeading();
  const language = getSupportedUserLanguage();

  const [
    { allSubscriptions },
    { getParsedMedsAlphabetically, getPrioritySubscription }
  ] = useBloc(SubscriptionCubit);

  const { subscription, parsedMeds, showMeds } = React.useMemo(() => {
    const sub = getPrioritySubscription();
    const meds = getParsedMedsAlphabetically(sub?.id);

    return {
      subscription: sub,
      parsedMeds: meds,
      showMeds: meds.length > 0
    };
  }, [allSubscriptions]);

  return (
    <Wrap>
      {!isHybridApp() && <AppTopSpacer pt />}
      <h1 className="only-sr" ref={headingRef} {...getHeadingProps()}>
        <Translate msg="homePage" />
      </h1>
      <Widgets>
        <ProfileWidget />
        <PoweredByWidget />
        <RightWidgets>
          <DailyReadingsWidget />
          <NotificationBell mobile />
        </RightWidgets>
      </Widgets>

      <Branding
        style={{
          marginBlockEnd: "1.6em"
        }}
      />

      <AppointmentsList />

      <TaskList
        programs={[
          KnownProgram.ONBOARDING,
          KnownProgram.CARE,
          KnownProgram.MEAL_PLAN,
          KnownProgram.LIFEBALANCE,
          KnownProgram.BEHAVIORAL_HEALTH_SCREENING,
          KnownProgram.EDUCATION,
          KnownProgram.NUTRITION_JOURNEY,
          KnownProgram.MINDSET_JOURNEY,
          KnownProgram.PHYSICAL_ACTIVITY_JOURNEY,
          KnownProgram.EVERYDAY_DIABETES_JOURNEY
        ]}
        consolidateOnboardingTasks
      />

      <MealInsightUploadCard />

      {showMeds && (
        <div style={{ marginTop: "1.5rem" }}>
          <MedicationSection
            parsedMeds={parsedMeds}
            subscription={subscription}
          />
        </div>
      )}

      <nine-spacer s="md" />
      <CollectionTitle>
        <Translate msg="articles.title" />
      </CollectionTitle>
      {language === "es" ? <UserEducationalFeedOld /> : <UserEducationalFeed />}
    </Wrap>
  );
};

export default DashboardPage;
